/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core";
import { FileCrawlingHistoryPane } from "./FileCrawlingHistoryPane";
import FileCrawlingErrorPane from "./FileCrawlingErrorPane";
import { FileCrawlingHistoriesApi } from "../../../helpers/api/notification-api";
import { AlertDialog } from "../../../components";
import {
  FileCrawlingErrorRow,
  FileCrawlingHistoryPaneData,
  FileCrawlingErrorPaneData,
} from "../../../types/notification";
import { AlertDialogData } from "../../../types";
import { errorToStr } from "../../../helpers";

/** スタイル定義 */
const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    bodySubcontainer: {
      // display: "flex",
      minWidth: 360,
    },
  })
);

/** Fileクロールコンテナ */
export function FileCrawlingContainer() {
  const classes = useStyles();

  //------------------------------
  // アラートダイアログ
  const [alertDialogData, setAlertDialogData] = React.useState<AlertDialogData>(
    {
      open: false,
      title: "",
      message: "",
    }
  );

  //------------------------------
  // 履歴画面関連
  // historyData    :履歴画面データ
  // [State]
  const [historyData, setHistoryData] = React.useState<
    FileCrawlingHistoryPaneData
  >({
    rows: [],
    currentPage: 1,
    totalPage: 0,
    displayCount: 10,
    totalCount: 0,
  });

  // [Effect]
  React.useEffect(() => {
    let cleanedUp = false;

    const fetchData = async () => {
      try {
        const res: any = await FileCrawlingHistoriesApi.list_by_page(
          historyData.currentPage,
          historyData.displayCount
        );
        if (!cleanedUp) {
          setHistoryData(prevData => ({
            ...prevData,
            rows: res.data.list,
            totalPage: res.data.info.total_page,
            displayCount: res.data.info.display_count,
            totalCount: res.data.info.total_count,
          }));
        }
      } catch (error: any) {
        if (!cleanedUp) {
          setAlertDialogData({
            open: true,
            title: "エラー",
            message: errorToStr(error),
          });
        }
      }
    };
    fetchData();

    return function cleanup() {
      cleanedUp = true;
    };
  }, []);

  /** 履歴画面ページ変更 */
  const handleChangeHistoryCurrentPage = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    FileCrawlingHistoriesApi.list_by_page(value, historyData.displayCount)
      .then((res: any) => {
        setHistoryData(prevData => ({
          ...prevData,
          rows: res.data.list,
          currentPage: value,
          totalPage: res.data.info.total_page,
          displayCount: res.data.info.display_count,
          totalCount: res.data.info.total_count,
        }));
        return true;
      })
      .catch(error => {
        setAlertDialogData({
          open: true,
          title: "エラー",
          message: errorToStr(error),
        });
      });
  };

  //------------------------------
  // エラー画面関連
  // errorDataFull    :1件の履歴に紐づくエラーデータ全件
  // errorDataDisplay :画面に表示するエラーデータ
  const [errorDataFull, setErrorDataFull] = React.useState<
    FileCrawlingErrorRow[]
  >([]);
  const [errorDataDisplay, setErrorDataDisplay] = React.useState<
    FileCrawlingErrorPaneData
  >({
    rows: [],
    currentPage: 1,
    totalPage: 0,
    displayCount: 5,
    totalCount: 0,
  });

  /** エラー画面ページ変更 */
  const handleChangeErrorCurrentPage = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    const sliceStart = errorDataDisplay.displayCount * (value - 1);
    const sliceEnd =
      errorDataDisplay.displayCount * value < errorDataDisplay.totalCount
        ? errorDataDisplay.displayCount * value
        : errorDataDisplay.totalCount;
    setErrorDataDisplay(prevData => ({
      ...prevData,
      rows: errorDataFull.slice(sliceStart, sliceEnd),
      currentPage: value,
    }));
  };

  //------------------------------
  // エラー画面表示関連
  // openErrorPane: エラー画面の表示状態
  const [openErrorPane, setOpenErrorPane] = React.useState(false);
  /** 表示状態切替 */
  const handleOpenErrorPaneChange = (id: number) => {
    const next = !openErrorPane;
    setOpenErrorPane(next);
    // エラー画面を閉じる場合は以降の処理は不要
    if (!next) {
      return;
    }
    FileCrawlingHistoriesApi.retrieve_only_error(id)
      .then((res: any) => {
        const totalCount = res.data.length;
        const totalPage = Math.ceil(totalCount / errorDataDisplay.displayCount);
        const sliceStart = 0;
        const sliceEnd =
          errorDataDisplay.displayCount < totalCount
            ? errorDataDisplay.displayCount
            : totalCount;
        setErrorDataFull(res.data);
        setErrorDataDisplay(prevData => ({
          ...prevData,
          rows: res.data.slice(sliceStart, sliceEnd),
          currentPage: 1,
          totalPage,
          totalCount,
        }));
        return true;
      })
      .catch(error => {
        setAlertDialogData({
          open: true,
          title: "エラー",
          message: errorToStr(error),
        });
      });
  };

  let component = <div>通知はありません。</div>;
  if (historyData.rows.length > 0) {
    component = openErrorPane ? (
      /** エラー画面表示 */
      <FileCrawlingErrorPane
        onChangeOpen={handleOpenErrorPaneChange}
        errorData={errorDataDisplay}
        onChangeCurrentPage={handleChangeErrorCurrentPage}
      />
    ) : (
      /** 履歴画面表示 */
      <FileCrawlingHistoryPane
        historyData={historyData}
        onChangeCurrentPage={handleChangeHistoryCurrentPage}
        onChangeOpenErrorPane={handleOpenErrorPaneChange}
      />
    );
  }

  //--------------------
  // コンポーネント
  return (
    <div>
      <div className={classes.bodySubcontainer}>{component}</div>
      <AlertDialog
        data={alertDialogData}
        handleClose={() => {
          setAlertDialogData((prevState: AlertDialogData) => {
            return { ...prevState, open: false };
          });
        }}
      />
    </div>
  );
}
